import { DELIVERY_LOCATION_TYPES } from 'src/models/delivery-location/DeliveryLocation'
import { DELIVERY_TYPE_OPTIONS } from 'src/models/delivery-type/DeliveryType'
import { CONDITIONS as CATEGORY_CONDITIONS, CONDITIONS_REQUIREMENTS as CATEGORY_CONDITIONS_REQUIREMENTS } from 'src/models/item/Category'
import { TYPES as SALES_CHANNEL_TYPES } from 'src/models/model/concerns/SalesChannelRestrictable'
import { useIsItemPackageAvailable } from 'src/modules/checkout/composables/sales-availability/item-packages/IsItemPackageAvailable'
import { useIsItemAvailable } from 'src/modules/checkout/composables/sales-availability/items/IsItemAvailable'
import { toValue } from 'vue'

// Options is a hash containing fields that can be used to validate category availability.
const DEFAULT_OPTIONS = Object.freeze({
  salesChannel: null,
  order: null
})

export function useIsCategoryAvailable () {
  const { isItemAvailable } = useIsItemAvailable()
  const { isItemPackageAvailable } = useIsItemPackageAvailable()

  function isCategoryAvailable (category, { salesChannel, order } = DEFAULT_OPTIONS) {
    const categoryValue = toValue(category)

    if (_.isNil(categoryValue)) return false

    const salesChannelValue = toValue(salesChannel)
    const orderValue = toValue(order)

    // The Category must be active (not archived) to be presented
    if (categoryValue.archivable && !categoryValue.isActive) return false

    // The Category must be available on the current Sales Channel
    if (!categoryValue.isSalesChannelEnabled(salesChannelValue)) return false

    if (_.includes(SALES_CHANNEL_TYPES.PRIVATE, salesChannelValue)) return true

    if (categoryValue.conditions[CATEGORY_CONDITIONS.DELIVERY_TYPES]) {
      if (categoryValue.isDeliveryTypeMatch(
        _.map(orderValue?.deliveryTypeOptions, 'value'),
        categoryValue.conditionsRequirements[CATEGORY_CONDITIONS_REQUIREMENTS.DELIVERY_TYPES],
        categoryValue.conditionsRequirements[CATEGORY_CONDITIONS_REQUIREMENTS.EXCLUDE_DELIVERY_TYPES]
      )) return false
    }

    // The Category must be available for the User
    // E.g. User meets any configured Membership restrictions (e.g. Membership Type, Tier, etc.)
    if (!categoryValue.availableForUser(orderValue?.user)) return false

    // currentShowing is the Showing currently in context
    // If there is no currentShowing, nextShowing is assumed to be the most relevant Showing
    const applicableShowing = orderValue?.currentShowing || orderValue?.nextShowing

    // TODO OOS INDY-6757 applicableShowing should be injected into the Composable using wider contextual state
    // e.g. We don't want Website Orders to permit Showing-specific promotional categories,
    // but we do want to resolve to such a Showing for QR Code Orders
    // Relying on the Order model to maintain contextual application data is fragile/unreliable

    // If there is an applicable Showing, the Category must be available for that Showing
    // If there is no applicable Showing, the Category must be available now
    if (!_.isNil(applicableShowing)) {
      if (!categoryValue.availableForShowing(applicableShowing)) return false
    } else {
      if (!categoryValue.availableNow) return false

      // TODO OOS INDY-6757 Specific validation logic should be extracted to a specialized Function/Composable
      if (categoryValue.conditions[CATEGORY_CONDITIONS.SHOWING_BADGES]) {
        if (orderValue?.deliveryType !== DELIVERY_TYPE_OPTIONS.THEATER_SEAT || _.isNil(orderValue?.deliveryLocation)) {
          // If there is no applicable Showing or Delivery Location, an INCLUSIVE Showing Badge condition cannot be met, as there are no Showing Badges
          if (!categoryValue.conditionsRequirements[CATEGORY_CONDITIONS_REQUIREMENTS.EXCLUDE_SHOWING_BADGES]) return false
        } else {
        // TODO OOS INDY-6757 Relevant configuration should be injected into the Composable
          const deliveryLocation = _.find(orderValue.configuration.deliveryLocations.data, { name: orderValue.deliveryLocation, type: DELIVERY_LOCATION_TYPES.THEATER_SEAT })
          if (_.isNil(deliveryLocation)) return false

          const showingBadgeIds = _.map(deliveryLocation?.screen?.computedShowingBadges, 'id')
          if (categoryValue.isShowingMatch(
            showingBadgeIds,
            categoryValue.showingBadgeIds,
            categoryValue.conditionsRequirements[CATEGORY_CONDITIONS_REQUIREMENTS.MATCH_SHOWING_BADGES],
            categoryValue.conditionsRequirements[CATEGORY_CONDITIONS_REQUIREMENTS.EXCLUDE_SHOWING_BADGES]
          )) return false
        }
      }
    }

    const options = {
      salesChannel: salesChannelValue,
      order: orderValue
    }
    const isAnyItemAvailable = _.some(categoryValue.items.data, item => isItemAvailable(item, options))
    // We include isAnyItemAvailable to avoid iterating over itemPackages when they are irrelevant to overall availability
    const isAnyItemPackageAvailable = isAnyItemAvailable || _.some(categoryValue.itemPackages.data, itemPackage => isItemPackageAvailable(itemPackage, options))

    if (!isAnyItemAvailable && !isAnyItemPackageAvailable) return false

    return true
  }

  return {
    isCategoryAvailable
  }
}
